import type { QueryClient, QueryKey } from '@tanstack/react-query'

import type { createQueryKeys } from './query-keys'
import type { Paged } from './pagination'

type Keys = ReturnType<typeof createQueryKeys>
type Snapshot = [QueryKey, unknown][]

/**
 * Helpers for optimistic updates: change what is on screen straight away, keep a copy of what was cached, and put it back if the
 * server says no. A resource is cached in three shapes (a page, a whole list, one item) and all three are kept in step.
 */
export function createOptimisticCache<TDto extends { id: string }>(keys: Keys) {
  return {
    /** Stop in-flight reads (they would overwrite the optimistic value) and remember everything cached for this resource. */
    async begin(queryClient: QueryClient): Promise<Snapshot> {
      await queryClient.cancelQueries({ queryKey: keys.root })
      return queryClient.getQueriesData({ queryKey: keys.root })
    },

    restore(queryClient: QueryClient, snapshot: Snapshot | undefined) {
      for (const [key, data] of snapshot ?? []) queryClient.setQueryData(key, data)
    },

    /** Replace one item wherever it is cached. */
    patch(queryClient: QueryClient, id: string, change: (item: TDto) => TDto) {
      const apply = (item: TDto) => (item.id === id ? change(item) : item)
      queryClient.setQueriesData<Paged<TDto>>({ queryKey: keys.lists() }, (old) => old && { ...old, items: old.items.map(apply) })
      queryClient.setQueriesData<TDto[]>({ queryKey: keys.alls() }, (old) => old?.map(apply))
      queryClient.setQueryData<TDto>(keys.detail(id), (old) => old && change(old))
    },

    /** Take one item out of every cached list, and forget its own entry. */
    drop(queryClient: QueryClient, id: string) {
      queryClient.setQueriesData<Paged<TDto>>({ queryKey: keys.lists() }, (old) => {
        if (!old) return old
        const items = old.items.filter((item) => item.id !== id)
        return { ...old, items, totalCount: Math.max(0, old.totalCount - (old.items.length - items.length)) }
      })
      queryClient.setQueriesData<TDto[]>({ queryKey: keys.alls() }, (old) => old?.filter((item) => item.id !== id))
      queryClient.removeQueries({ queryKey: keys.detail(id) })
    },
  }
}
